import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService } from '../services/apiService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      // First check if backend is running
      try {
        await apiService.healthCheck();
        setConnectionError(false);
      } catch (error) {
        console.error('Backend connection failed:', error);
        setConnectionError(true);
        setLoading(false);
        return;
      }

      // Then check authentication
      if (apiService.isAuthenticated()) {
        try {
          const userData = await apiService.getCurrentUser();
          setUser(userData);
        } catch (error) {
          // Token is invalid, clear it
          apiService.logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (credentials) => {
    console.log('AuthContext: Attempting login...');
    const response = await apiService.login(credentials);
    console.log('AuthContext: Login successful, setting user:', response.user);
    setUser(response.user);
    return response;
  };

  const register = async (userData) => {
    console.log('AuthContext: Attempting registration...');
    const response = await apiService.register(userData);
    console.log('AuthContext: Registration successful, setting user:', response.user);
    setUser(response.user);
    return response;
  };

  const logout = () => {
    apiService.logout();
    setUser(null);
  };

  const updateUser = async (updates) => {
    const updatedUser = await apiService.updateUser(updates);
    setUser(updatedUser);
    return updatedUser;
  };

  const setDefaultList = async (listId) => {
    const updatedUser = await updateUser({ defaultListId: listId });
    return updatedUser;
  };

  const value = {
    user,
    loading,
    connectionError,
    login,
    register,
    logout,
    updateUser,
    setDefaultList,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
